import React, { useContext, useState } from 'react';

import { dashboardInvoiceApis } from '@apis/next/invoiceApis';
import { BookingDetailsContext } from '@components/admin/booking/helpers';
import { InvoiceDetailsFormDialog } from '@components/admin/booking/InvoiceDetailsFormDialog';
import { InvoiceDetailsTable } from '@components/admin/booking/InvoiceDetailsTable';
import { Button } from '@components/Button';
import { HorizontalSpace } from '@components/HorizontalSpace';
import { QueryContainer } from '@components/QueryContainer';
import { VerticalSpace } from '@components/VerticalSpace';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { AdminInquiryDetailHelper } from '@models/BookingInquiry';
import { InvoiceDetails } from '@models/next/Invoice';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { foldMessagesEither } from '@utils/errors';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import Color from 'color';

const strings = createStrings({
  ja: {
    invoiceAddButton: '請求名目を追加する',
    chinaChargeNotChecked: '中国費用检查（待确认）',
    chinaChargeChecked: '中国費用检查（已确认）',
    tradeConditionWarningCN: () => (
      <>
        注意: <br />
        【预约内容】与【prealert】的【贸易条件/payment
        terms】不一致，【请求明细】根据【预约内容】生成，如有需要请修改【预约内容】中的【贸易条件】。
      </>
    ),
    tradeConditionWarningJA: () => (
      <>
        注意: <br />
        請求明細の請求先や数量などの初期値を変更したい場合は、 「予約内容」
        に紐付けられていますので、[予約内容] 画面で変更してください。
        <br />
        如需修改请求明细的数量和请求先的默认值，因请求明细的默认值与【预约内容】绑定，请进入【预约内容】画面更改。
      </>
    ),
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  alertWarning: {
    ...common.smallerText,
    padding: 10,
    border: `1px solid ${theme.palette.warning.dark}`,
    backgroundColor: Color(theme.palette.warning.dark).alpha(0.1).toString(),
  },
  alertPrimary: {
    ...common.smallerText,
    padding: 10,
    border: `1px solid ${theme.palette.primary.dark}`,
    backgroundColor: Color(theme.palette.primary.dark).alpha(0.1).toString(),
  },
}));

export const InvoiceDetailsPanel: React.FC = () => {
  const classes = { ...useStyles(), ...useCommonStyles() };

  const { user, preAlert, bookingInquiry } = useContext(BookingDetailsContext);

  const [invoiceDetailDialogOpen, setInvoiceDetailsDialogOpen] = useState(false);
  const invoiceDetails = dashboardInvoiceApis.useGet(
    '/dashboard/booking-inquiry/:d2d_no/invoice-detail',
    {
      params: {
        d2d_no: bookingInquiry.d2d_no,
      },
      queries: { show_all: true },
    },
  );

  const [currentInvoiceDetail, setCurrentInvoiceDetail] = useState<InvoiceDetails | undefined>();

  const canEdit = new AdminInquiryDetailHelper(bookingInquiry).canEdit(user, preAlert);

  const { confirm, renderDialog } = useConfirmDialog();

  return (
    <>
      {renderDialog()}

      <QueryContainer queries={[invoiceDetails]}>
        {preAlert.payments_terms !== bookingInquiry.trade_condition && (
          <>
            <div className={classes.alertWarning}>{strings.tradeConditionWarningCN()}</div>
            <div style={{ height: 10 }} />
            <div className={classes.alertPrimary}>{strings.tradeConditionWarningJA()}</div>
            <div style={{ height: 10 }} />
          </>
        )}

        {invoiceDetails.data && invoiceDetails.data.length > 0 && (
          <InvoiceDetailsTable
            type="panel"
            color="grey"
            items={invoiceDetails.data}
            onClickEdit={(item) => {
              setCurrentInvoiceDetail(item);
              setInvoiceDetailsDialogOpen(true);
            }}
          />
        )}

        <InvoiceDetailsFormDialog
          open={invoiceDetailDialogOpen}
          onClose={() => setInvoiceDetailsDialogOpen(false)}
          item={currentInvoiceDetail}
        />

        <VerticalSpace height={16} />

        <div className={clsx(classes.flexInline, classes.justifyStart)}>
          <Button
            size="small"
            shape="round"
            text={strings.invoiceAddButton}
            color={foldMessagesEither(
              canEdit,
              () => 'grey',
              () => 'primary',
            )}
            onClick={() =>
              foldMessagesEither(
                canEdit,
                ({ messages }) => {
                  confirm({ message: messages });
                },
                () => {
                  setCurrentInvoiceDetail(undefined);
                  setInvoiceDetailsDialogOpen(true);
                },
              )
            }
            type="button"
          />
          <HorizontalSpace width={16} />
        </div>
      </QueryContainer>
    </>
  );
};
